// "Add a trade" → Import a file: pick the platform, follow its export steps, drop or choose the
// file. The file is read on the Trades page (Journal.js), which shows the import preview.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from './i18n';
import { EXPORTS, STOCK_EXPORTS } from './exportSteps';
import { TEMPLATE_HREF } from './csvTemplate';

const OTHER = 'Any other platform';
const PLATFORMS = [...EXPORTS.filter((p) => p.name !== OTHER), ...STOCK_EXPORTS, ...EXPORTS.filter((p) => p.name === OTHER)];
const ACCEPT = '.csv,text/csv,.txt,.tsv,.xls,.xlsx,.pdf';
const PICTURES = `${ACCEPT},image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp`;

export default function AddTradeImport({ onFile, pictures, picturesPreview }) {
  const { t } = useI18n();
  const [name, setName] = useState(PLATFORMS[0].name);
  const [dragging, setDragging] = useState(false);
  const platform = PLATFORMS.find((p) => p.name === name) || PLATFORMS[0];
  // Screenshots and PDFs can come several at a time (they're read with AI); a CSV is one file.
  const pick = (files) => files && files.length && onFile({ target: { files: pictures ? [...files] : [files[0]] } });

  return (
    <div className="add-import">
      <span className="modal-section-label">{t('Where are your trades?')}</span>
      <div className="add-import-platforms" role="group" aria-label={t('Platform')}>
        {PLATFORMS.map((p) => (
          <button key={p.name} type="button" className={p.name === name ? 'active' : ''} aria-pressed={p.name === name} onClick={() => setName(p.name)}>
            {t(p.name)}
          </button>
        ))}
      </div>

      {platform.name === 'TopstepX' && (
        <p className="add-import-sync">
          <strong>{t('Skip the files:')}</strong> {t('connect TopstepX live sync and your trades arrive by themselves every 15 minutes.')}{' '}
          <Link to="/app/accounts/new">{t('Connect TopstepX')} &rarr;</Link>
        </p>
      )}

      <ol className="add-import-steps">
        {platform.steps.map((step) => <li key={step}>{t(step)}</li>)}
      </ol>

      <label className={`add-import-drop${dragging ? ' dragging' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files); }}>
        <input type="file" accept={pictures ? PICTURES : ACCEPT} multiple={pictures} onChange={(e) => pick(e.target.files)} />
        <strong>{t('Choose your file')}</strong>
        <span>{t('or drag it here')}</span>
        {pictures && <span className="add-import-pictures">{t('No CSV? Drop screenshots or a PDF of your trade history and AI reads the trades.')} {picturesPreview && <span className="admin-preview-badge">{t('Admin only')}</span>}</span>}
      </label>

      <details className="add-import-own">
        <summary>{t('Using your own spreadsheet?')}</summary>
        <p className="field-hint">
          {t('Columns needed:')} <code>symbol</code>, <code>entryPrice</code>, <code>exitPrice</code>.{' '}
          {t('Optional:')} <code>side</code>, <code>size</code>, <code>pnl</code>, <code>fees</code>, <code>date</code>, <code>notes</code>.{' '}
          <a href={TEMPLATE_HREF} download="trades-template.csv">{t('Download a template')}</a>
        </p>
      </details>
      <p className="field-hint">
        {t('Platform not listed, or the file won’t import?')}{' '}
        <Link to="/contact?topic=platform">{t('Tell us and we’ll add it')} &rarr;</Link>
      </p>
    </div>
  );
}
